

import React, { useState, useEffect } from 'react';
import { BsInfoCircleFill } from "react-icons/bs";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const PriceSummary = ({
  subTotal = 0,
  gstTotal = 0,
  platformCharges = 0,
  packingCharges = 0,
  deliveryCharges = 0,
  discountAmount: propDiscountAmount = 0,
  grandTotal: propGrandTotal = 0,
  appliedCoupon: propAppliedCoupon = null,
  onRemoveCoupon,
  onApplyCoupon,
  onShowGST,
  cartId: propCartId,
  fetchCartData,
  userId: propUserId,
  token: propToken
}) => {
  const navigate = useNavigate();
  const [localAppliedCoupon, setLocalAppliedCoupon] = useState(null);
  const [localDiscountAmount, setLocalDiscountAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [localGrandTotal, setLocalGrandTotal] = useState(propGrandTotal);

  // Use props if provided, otherwise use local state
  const appliedCoupon = propAppliedCoupon !== null ? propAppliedCoupon : localAppliedCoupon;
  const discountAmount = propDiscountAmount !== 0 ? propDiscountAmount : localDiscountAmount;
  const grandTotal = propGrandTotal !== 0 ? propGrandTotal : localGrandTotal;

  // Load saved coupon from localStorage on mount
  useEffect(() => {
    const savedCoupon = localStorage.getItem('appliedCoupon');
    if (savedCoupon && !propAppliedCoupon) {
      const coupon = JSON.parse(savedCoupon);
      setLocalAppliedCoupon(coupon);
      setLocalDiscountAmount(coupon.discountAmount || 0);
    }
  }, [propAppliedCoupon]);

  // Update grand total when values change
  useEffect(() => {
    if (propGrandTotal === 0) {
      const calculatedGrandTotal = 
        (subTotal || 0) + 
        (gstTotal || 0) + 
        (platformCharges || 0) + 
        (packingCharges || 0) + 
        (deliveryCharges || 0) - 
        (discountAmount || 0);
      setLocalGrandTotal(calculatedGrandTotal);
    }
  }, [subTotal, gstTotal, platformCharges, packingCharges, deliveryCharges, discountAmount, propGrandTotal]);

  // Force refresh cart data when coupon is applied from notifications
  useEffect(() => {
    const handleStorageChange = async (e) => {
      if (e.key === 'couponApplied' && e.newValue) {
        const couponData = JSON.parse(e.newValue);
        if (couponData && couponData.code && fetchCartData) {
          await fetchCartData();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check for appliedCoupon changes
    const checkCouponInterval = setInterval(async () => {
      const savedCoupon = localStorage.getItem('appliedCoupon');
      if (savedCoupon && !appliedCoupon && fetchCartData) {
        await fetchCartData();
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkCouponInterval);
    };
  }, [fetchCartData, appliedCoupon]);

  // Handle Apply Coupon click
  const handleApplyCoupon = () => {
    if (onApplyCoupon) {
      onApplyCoupon();
    } else {
      navigate('/notifications', { 
        state: { returnTo: 'cart' } 
      });
    }
  };

  // Handle Remove Coupon click
 const handleRemoveCoupon = async () => {
  if (onRemoveCoupon) {
    onRemoveCoupon();
  } else {
    const token = propToken || localStorage.getItem("authToken");
    const userId = propUserId || localStorage.getItem("userId");
    
    if (!userId || !token) {
      alert("❌ Please login again!");
      return;
    }

    let activeCartId = propCartId || localStorage.getItem("cartId");
    
    if (activeCartId) {
      setLoading(true);
      try {
        // ✅ CORRECT REQUEST BODY FOR REMOVING COUPON
        const requestBody = {
          applyCoupon: "NOT_APPLIED"  // ✅ Change from "REMOVED" to "NOT_APPLIED"
        };

        console.log("Removing coupon with body:", requestBody);

        const response = await axios.put(
          `https://backend.maamaas.com/food/api/cart/coupon/${activeCartId}`,
          requestBody,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              Accept: "application/json"
            },
          }
        );

        console.log("Coupon remove response:", response.data);

        if (response.status === 200) {
          setLocalAppliedCoupon(null);
          setLocalDiscountAmount(0);
          localStorage.removeItem('appliedCoupon');
          
          // ✅ Clear coupon data from localStorage
          localStorage.removeItem('appliedCoupon');
          
          // ✅ Trigger storage event
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'couponRemoved',
            newValue: JSON.stringify({ removed: true })
          }));
          
          alert("✅ Coupon removed successfully!");
          
          // ✅ Refresh cart data
          if (fetchCartData) {
            await fetchCartData();
          } else {
            window.location.reload();
          }
        } else {
          alert("⚠️ Failed to remove coupon");
        }
      } catch (error) {
        console.error('Error removing coupon:', error);
        
        if (error.response) {
          console.log("Error response:", error.response.data);
          alert(`❌ ${error.response.data?.message || "Failed to remove coupon"}`);
        } else {
          alert("❌ Failed to remove coupon");
        }
      } finally {
        setLoading(false);
      }
    }
  }
};

  const priceRows = [
    { label: 'Sub Total', value: subTotal || 0 },
    (platformCharges || 0) > 0 && { label: 'Platform Charges', value: platformCharges || 0 },
    (packingCharges || 0) > 0 && { label: 'Packing Charges', value: packingCharges || 0 },
    (deliveryCharges || 0) > 0 && { label: 'Delivery Charges', value: deliveryCharges || 0 },
    (discountAmount || 0) > 0 && { label: 'Discount', value: discountAmount || 0, green: true },
  ].filter(Boolean);

  return (
    <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
      {/* GST Row */}
      <div className="price-row" style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 0',
        fontSize: '14px',
        alignItems: 'center'
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          GST
          <BsInfoCircleFill
            onClick={onShowGST}
            style={{ fontSize: '14px', color: '#ee6521', cursor: 'pointer' }}
          />
        </span>
        <span>₹{(gstTotal || 0).toFixed(2)}</span>
      </div>

      {/* Other Price Rows */}
      {priceRows.map((row, i) => (
        <div key={i} style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '8px 0',
          fontSize: '14px',
          color: row.green ? '#22c55e' : '#555'
        }}>
          <span>{row.label}</span>
          <span>{row.green ? '-' : ''}₹{Math.abs(row.value).toFixed(2)}</span>
        </div>
      ))}

      {/* Apply Coupon Button Section */}
      <div style={{ margin: '16px 0' }}>
        {!appliedCoupon ? (
          <button
            onClick={handleApplyCoupon}
            disabled={loading}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ee6521',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              display: 'inline-block',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Applying...' : 'Apply Coupon'}
          </button>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
             
             
              borderRadius: '10px'
            }}
          >
            {/* <span style={{ color: '#22c55e', fontWeight: '500' }}>
              🎉 Coupon Applied! Saved ₹{(discountAmount || 0).toFixed(2)}
            </span> */}
            <button
              onClick={handleRemoveCoupon}
              disabled={loading}
              style={{
                padding: '6px 14px',
                backgroundColor: '#5ddc26',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Removing...' : 'Applied'}
            </button>
          </div>
        )}
      </div>

      {/* Grand Total */}
      <div style={{
        borderTop: '2px solid #ddd',
        margin: '16px 0',
        paddingTop: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '18px',
        fontWeight: 'bold'
      }}>
        <span>Grand Total</span>
        <span style={{ color: '#ee6521' }}>₹{(grandTotal || 0).toFixed(2)}</span>
      </div>
    </div>
  );
};

export default PriceSummary;